import {
  Alert,
  Box,
  CircularProgress,
  Snackbar,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import MenuOpenIcon from "@mui/icons-material/MenuOpenOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesomeOutlined";
import WarningIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { reorderIds } from "@shared/catalogue-order";
import { foyerApi, type PremiseRails } from "../../http/foyer";
import { friendlyError } from "../../utils/apiError";
import { tokens } from "../../theme";

/**
 * The menu, as a hotel sees it — Streaming TV → Menu.
 *
 * ## 🚨 There is no "page" object here
 *
 * A carousel is the only thing. Ticking one into the menu (`page.enabled`)
 * makes it ONE MENU ROW, and opening that row shows the carousels inside it
 * (`page.carousels`). Sigge's words, and the shape of the whole feature:
 *
 * > *"Movies as a rail on main menu would be a curated list of top sellers.
 * >  Movies in the menu will open Action, Oldies but goldies, Marvel and
 * >  whatever genre we have come up with in the dynamic carousels."*
 *
 * An earlier version of this file modelled the menu as `SubPage` rows a person
 * added — a second object with its own name, which could drift from the rail it
 * was named after. That is the model this one replaces, and it is why nothing
 * here creates or deletes a page.
 *
 * ## Who may change what, and why the line is there
 *
 * | Decision | Who | Stored on |
 * |---|---|---|
 * | the ORDER rails and menu rows appear in | the hotel | `Premise.carousels` |
 * | hiding a rail here | the hotel | `Premise.disabledCarouselIds` |
 * | whether a rail IS a menu row | Nenda | `Carousel.page.enabled` |
 * | what a menu row opens | Nenda | `Carousel.page.carousels` |
 *
 * Ruled by Sigge: *"they will not decide WHICH rails, just in WHICH ORDER."*
 * The reason is not taste — `canManageDistribution` is `adminOnly` precisely
 * because a catalogue row carries supplier terms and stream URLs — and the
 * consequence is real: `page.enabled` lives on the GLOBAL carousel, so ticking
 * it lands on every customer holding that rail, not just this one.
 *
 * The menu inherits the hotel's order for free: the menu IS the rail set
 * filtered to `page.enabled`, so dragging a rail up moves its menu row with it.
 *
 * What Nenda decides (which rows are menu rows, and what each opens) is SHOWN
 * to everyone as read-only structure: a menu row's carousels are drawn nested
 * under it, so a hotel can see why its menu is what it is without a control
 * that would promise a per-hotel edit.
 *
 * ## ⚠️ The drag has no automated coverage
 *
 * jsdom has no pointer geometry, and dnd-kit's keyboard sensor cannot be driven
 * against zero-sized rects — both dead ends recorded in
 * `PickedTitlesList.test.tsx`. The order maths (`reorderIds`) and the save
 * shape are covered; the gesture needs a human.
 */

export interface MenuComposerProps {
  premiseId: string;
}

export default function MenuComposer({ premiseId }: MenuComposerProps) {
  /*
   * ONE column since 2026-09-29 (Sigge). The two explanatory boxes and the
   * right-hand preview pane are gone: "just confusing". What the preview was
   * there to answer - what does a menu row OPEN? - is now drawn in the list
   * itself, as the carousels nested under that row. That also removed this
   * screen's read of the global carousel catalogue, which a customer may not
   * make (supplier terms, stream URLs) and which had turned the page into a
   * 403 for every premise admin.
   */
  const [data, setData] = useState<PremiseRails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await foyerApi.premiseRails(premiseId));
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [premiseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rails = useMemo(() => data?.rails ?? [], [data]);

  /** Local order, so a drag reads instantly and the save reconciles after. */
  const [local, setLocal] = useState<string[] | null>(null);
  const order = local ?? rails.map((r) => r.id);
  useEffect(() => setLocal(null), [data]);
  const railById = useMemo(() => new Map(rails.map((r) => [r.id, r])), [rails]);
  const ordered = order.flatMap((id) => {
    const r = railById.get(id);
    return r ? [r] : [];
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  async function save(next: string[]) {
    setError(null);
    try {
      setData(await foyerApi.savePremiseRails(premiseId, { carousels: next }));
      setSaved(true);
    } catch (e) {
      setError(friendlyError(e));
      // Re-read rather than keeping an order the server refused: a menu that
      // disagrees with what is stored is the one state nobody can act on.
      void load();
    }
  }

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return;
    const next = reorderIds(order, String(active.id), String(over.id));
    // `reorderIds` returns null on a no-op or an unknown id, so a stray drop
    // cannot write a spurious order.
    if (!next) return;
    setLocal(next);
    void save(next);
  };

  function toggleHidden(id: string, hide: boolean) {
    const current = rails.filter((r) => r.disabled).map((r) => r.id);
    const next = hide
      ? [...new Set([...current, id])]
      : current.filter((other) => other !== id);
    setError(null);
    void foyerApi
      .savePremiseRails(premiseId, { disabledCarouselIds: next })
      .then((res) => {
        setData(res);
        setSaved(true);
      })
      .catch((e) => {
        setError(friendlyError(e));
        void load();
      });
  }

  return (
    <>
      {loading && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={22} />
        </Stack>
      )}
      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && data && (
        <Box sx={{ maxWidth: 640 }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={order}
              strategy={verticalListSortingStrategy}
            >
              <Stack spacing={1} data-testid="menu-rails">
                {ordered.map((rail, index) => (
                  <RailNode
                    key={rail.id}
                    rail={rail}
                    index={index}
                    onToggleHidden={(hide) => toggleHidden(rail.id, hide)}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>

          {ordered.length === 0 && (
            <Typography sx={{ color: tokens.inkMuted, fontSize: 14 }}>
              This hotel has no rows yet. They arrive when a product is sold, or
              when somebody adds one.
            </Typography>
          )}
        </Box>
      )}

      <Snackbar
        open={saved}
        autoHideDuration={2000}
        onClose={() => setSaved(false)}
        message="Saved"
      />
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

type Rail = PremiseRails["rails"][number];

function RailNode({
  rail,
  index,
  onToggleHidden,
}: {
  rail: Rail;
  index: number;
  onToggleHidden: (hide: boolean) => void;
}) {
  const sortable = useSortable({ id: rail.id });
  const onPointerDown = sortable.listeners?.onPointerDown as
    | ((e: ReactPointerEvent) => void)
    | undefined;
  const style: CSSProperties = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.4 : rail.disabled ? 0.55 : 1,
  };
  const opens = rail.menu?.opens ?? [];

  return (
    <Box
      ref={sortable.setNodeRef}
      style={style}
      data-testid={`menu-rail-${index}`}
      sx={{
        border: "1px solid",
        borderColor: tokens.divider,
        borderRadius: "12px",
        bgcolor: tokens.surface,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        onPointerDown={onPointerDown}
        {...sortable.attributes}
        aria-label={`Reorder ${rail.name || rail.id}`}
        sx={{
          px: 1.5,
          py: 1.25,
          // The whole row drags — a list whose only target is a small handle
          // looks draggable and is not.
          cursor: "grab",
          touchAction: "none",
        }}
      >
        <DragIndicatorIcon fontSize="small" sx={{ color: tokens.inkFaint }} />
        <Typography
          sx={{
            width: 20,
            textAlign: "right",
            fontSize: 12,
            fontVariantNumeric: "tabular-nums",
            color: tokens.inkFaint,
          }}
        >
          {index + 1}
        </Typography>

        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography sx={{ fontWeight: 600, fontSize: 14 }} noWrap>
              {rail.name || rail.id}
            </Typography>
            {rail.missing && (
              <Tooltip title="No carousel answers to this id, so this row draws nothing.">
                <WarningIcon fontSize="small" sx={{ color: tokens.warning }} />
              </Tooltip>
            )}
          </Stack>
          <Typography sx={{ color: tokens.inkFaint, fontSize: 12 }}>
            {rail.menu
              ? rail.menu.hiddenFromHome
                ? "In the menu only"
                : "On the home screen and in the menu"
              : rail.source === "product"
                ? `Added by ${rail.productName ?? "a product"}`
                : "A row on the home screen"}
          </Typography>
        </Stack>

        {rail.source === "product" && (
          <Tooltip
            title={`Added by ${rail.productName ?? "a product"} — it appears wherever that product is sold.`}
          >
            <AutoAwesomeIcon fontSize="small" sx={{ color: tokens.inkFaint }} />
          </Tooltip>
        )}

        <Tooltip
          title={
            rail.disabled
              ? "Show this row at this hotel"
              : "Hide this row at this hotel — everywhere else keeps it"
          }
        >
          <Switch
            size="small"
            checked={!rail.disabled}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onToggleHidden(!e.target.checked)}
            inputProps={{ "aria-label": `Show ${rail.name || rail.id}` }}
          />
        </Tooltip>
      </Stack>

      {/*
        What a menu row OPENS, nested under it so a card that holds carousels
        reads as one (Sigge, 2026-09-29) - this replaced the preview pane and
        the explanation boxes. Read-only for everybody: the body is authored on
        the carousel and is the same at every hotel, so a control here would
        promise a per-hotel edit that does not exist.
      */}
      {rail.menu && (
        <Box
          data-testid={`menu-rail-${index}-opens`}
          sx={{
            borderTop: "1px solid",
            borderColor: tokens.divider,
            bgcolor: tokens.rowAlt,
            px: 1.5,
            py: 1,
            pl: 6.5,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ mb: 0.75 }}
          >
            <MenuOpenIcon sx={{ fontSize: 15, color: tokens.inkMuted }} />
            <Typography
              sx={{ fontSize: 12, fontWeight: 600, color: tokens.inkMuted }}
            >
              {opens.length === 0
                ? "Opens a menu page with no carousels yet"
                : `Opens ${opens.length} ${
                    opens.length === 1 ? "carousel" : "carousels"
                  }`}
            </Typography>
          </Stack>
          {opens.length > 0 && (
            <Stack
              spacing={0.5}
              sx={{
                // The tree line: these belong to the card above, not to the
                // home screen.
                borderLeft: "2px solid",
                borderColor: tokens.divider,
                pl: 1.5,
              }}
            >
              {opens.map((child) => (
                <Stack
                  key={child.id}
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  data-testid={`menu-rail-${index}-opens-${child.id}`}
                >
                  <Typography sx={{ fontSize: 13 }} noWrap>
                    {child.name || child.id}
                  </Typography>
                  {child.missing && (
                    <Tooltip title="No carousel answers to this id — this row draws nothing.">
                      <WarningIcon
                        sx={{ fontSize: 14, color: tokens.warning }}
                      />
                    </Tooltip>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
}
